import { useState, useEffect, useRef } from 'react'
import cn from 'classnames'
import { InputRange } from 'components/ui/input-range'
import ButtonBlue from 'components/ui/button/_color/_blue'

import { formatNumber } from 'utils/formatters'

import { useTranslation } from 'utils/use-translation'

import onlyNumberChanger from '../../../../utils/only-numbers-value'

import styles from './styles.module.scss'

const InviteCalculator = ({ handleGoToRefferal }) => {
  const { t } = useTranslation()

  const [loanAmount, setLoanAmount] = useState(3000)
  const [referralsAmount, setReferralsAmount] = useState(10000)
  const [loanDuration, setLoanDuration] = useState(4)
  const [resultValue, setResultValue] = useState('')

  const [formattedLoanAmount, setFormattedLoanAmount] = useState(formatNumber(loanAmount.toString()))
  const [formattedReferralsAmount, setFormattedReferralsAmount] = useState(formatNumber(referralsAmount.toString()))
  const [formattedLoanDuration, setFormattedLoanDuration] = useState(formatNumber(loanDuration.toString()))

  const loanAmountInputRef = useRef<HTMLInputElement>()
  const referralAmountInputRef = useRef<HTMLInputElement>()
  const loanDurationInputRef = useRef<HTMLInputElement>()

  const loanMax = 100000
  const loanMin = 100
  const apr = 0.024

  useEffect(() => {
    const result = Math.round(((loanAmount * referralsAmount * apr) / 12) * loanDuration).toString()
    setResultValue(result.replace(/(\d)(?=(\d\d\d)+([^\d]|$))/g, '$1 '))
  }, [loanAmount, loanDuration, referralsAmount])

  const handleChangeValue = (value, setValue, maxValue) => {
    if (value.target.value === '') {
      setValue('0')
      return
    }

    const processedValue = value.target.value.toString().replace(/\s/g, '')
    const filteredValue = processedValue.replace(/\D/g, '')

    const finalValue = Math.min(parseInt(filteredValue), maxValue)

    setValue(finalValue.toString())
  }

  const handleChangeAmount = (event, setValue, formattedValue, setFormattedValue, inputRef, maxValue) => {
    const rawValue = event.target.value.replace(/\s/g, '')

    let cursorPosition = event.target.selectionStart
    let newValue = ''

    if (event.nativeEvent.inputType === 'deleteContentBackward') {
      cursorPosition += 1
    } else if (event.nativeEvent.inputType === 'deleteContentForward') {
      cursorPosition += 1
    } else {
      cursorPosition -= 1
    }

    onlyNumberChanger(rawValue, (newRawValue) => {
      newValue = newRawValue

      const newFormattedValue = formatNumber(newValue)
      setFormattedValue(newFormattedValue)

      setTimeout(() => {
        if (inputRef.current) {
          const newCursorPosition = cursorPosition - (formattedValue.length - newFormattedValue.length)
          inputRef.current.setSelectionRange(newCursorPosition, newCursorPosition)
        }
      }, 0)
    })
    onlyNumberChanger(rawValue, () => handleChangeValue(event, setValue, maxValue))
  }

  const handleChangeAmountRange = (val: number, setFormattedValue, setValue) => {
    setFormattedValue(formatNumber(val))
    setValue(val)
  }

  return (
    <div className={styles.wrapper}>
      <div className={styles.sliderContainer}>
        <label htmlFor="loan" className={styles.inputLabel}>
          {t('loan_amount')}
        </label>
        <div className={styles.rangeWrapper}>
          <InputRange
            maxValue={loanMax}
            minValue={loanMin}
            step={1}
            value={formattedLoanAmount}
            onChange={(e) => handleChangeAmountRange(e, setFormattedLoanAmount, setLoanAmount)}
            mainColor={t('blue_hex')}
            secondColor={t('blue_hex')}
            isPinV2
          />
        </div>
        <output name="loanAmount" id="loanAmount">
          <input
            type="text"
            className={cn(styles.input, styles.inputLoan)}
            value={formattedLoanAmount}
            step={1}
            max={loanMax}
            min={loanMin}
            onChange={(e) =>
              handleChangeAmount(
                e,
                setLoanAmount,
                formattedLoanAmount,
                setFormattedLoanAmount,
                loanAmountInputRef,
                loanMax,
              )
            }
          />{' '}
          <span className={styles.inputSuffix}>{t('usdt')}</span>
        </output>
      </div>

      <div className={styles.sliderContainer}>
        <label htmlFor="customer" className={styles.inputLabel}>
          {t('referrals_amount')}
        </label>
        <div className={styles.rangeWrapper}>
          <InputRange
            maxValue={100000}
            minValue={10}
            step={10}
            value={formattedReferralsAmount}
            onChange={(referralsAmount) =>
              handleChangeAmountRange(referralsAmount, setFormattedReferralsAmount, setReferralsAmount)
            }
            mainColor={t('blue_hex')}
            secondColor={t('blue_hex')}
            isPinV2
          />
        </div>
        <output name="customerAmount" id="customerAmount" htmlFor="customer">
          <input
            type="text"
            className={cn(styles.input, styles.inputCustomer)}
            value={formattedReferralsAmount}
            step={10}
            max={100000}
            min={10}
            onChange={(e) =>
              handleChangeAmount(
                e,
                setReferralsAmount,
                formattedReferralsAmount,
                setFormattedReferralsAmount,
                referralAmountInputRef,
                100000,
              )
            }
          />
        </output>
      </div>

      <div className={styles.sliderContainer}>
        <label htmlFor="term" className={styles.inputLabel}>
          {t('loan_duration_capital')}
        </label>
        <div className={styles.rangeWrapper}>
          <InputRange
            maxValue={24}
            minValue={2}
            step={1}
            value={formattedLoanDuration}
            onChange={(loanDuration) =>
              handleChangeAmountRange(loanDuration, setFormattedLoanDuration, setLoanDuration)
            }
            mainColor={t('blue_hex')}
            secondColor={t('blue_hex')}
            isPinV2
          />
        </div>
        <output name="termAmount" id="termAmount" htmlFor="term">
          <input
            type="text"
            className={cn(styles.input)}
            value={formattedLoanDuration}
            step={1}
            max={24}
            min={2}
            onChange={(e) =>
              handleChangeAmount(e, setLoanDuration, loanDuration, setFormattedLoanDuration, loanDurationInputRef, 24)
            }
          />
          <span className={styles.inputSuffix}>{t('loan_duration_months')}</span>
        </output>
      </div>

      <div className={styles.info}>
        <div className={styles.infoTitle}>
          {t('in')} {loanDuration} {t('months_you_will_make')}
        </div>
        <div className={styles.infoValue}>{resultValue} USDT</div>
      </div>

      <ButtonBlue className={styles.button} onClick={handleGoToRefferal}>
        {t('start_inviting')}
      </ButtonBlue>
    </div>
  )
}

export default InviteCalculator
